import { zodResolver } from '@hookform/resolvers/zod';
import { useAppConfig } from '@plato/app-core/hooks/use-app-config';
import { useFormExtended, type UseFormExtendedReturn } from '@plato/ui-legacy/lib/hooks/use-form-extended';
import { useMemo } from 'react';
import z from 'zod';

export const allTransactionTypes = ['ORDER', 'QUOTE'] as const;
export type TransactionType = (typeof allTransactionTypes)[number];

export type DraftFormValues = {
    billing_address_id: string;
    contact_person_id: string;
    customer_id: string;
    delivery_date: string;
    draft_order_id: string;
    document_type: string;
    due_date: string;
    notes: string;
    po_number: string;
    shipping_address_id: string;
    shipping_type_id: string;
    transaction_type: TransactionType;
};

export type UseDraftFormOptions = {
    disabled: boolean;
};

export function useDraftForm({
    disabled,
}: UseDraftFormOptions): UseFormExtendedReturn<DraftFormValues, unknown> {
    const { transactionDraftTypes = allTransactionTypes, transactionHeaderExtraRequiredFields } =
        useAppConfig();
    const isPoNumberRequired = transactionHeaderExtraRequiredFields.includes('po_number');
    const isContactPersonRequired = transactionHeaderExtraRequiredFields.includes('contact_person_id');

    const draftFormSchema = useMemo(
        () =>
            z.object({
                billing_address_id: z.string().min(1),
                contact_person_id: z.string().min(isContactPersonRequired ? 1 : 0),
                customer_id: z.string().min(1),
                delivery_date: z.string().min(1),
                document_type: z.string().min(1),
                draft_order_id: z.string().min(1),
                due_date: z.string().min(1),
                notes: z.string(),
                po_number: z.string().min(isPoNumberRequired ? 1 : 0),
                shipping_address_id: z.string().min(1),
                shipping_type_id: z.string().min(1),
                transaction_type: z.enum(allTransactionTypes),
            }),
        [isContactPersonRequired, isPoNumberRequired],
    );

    return useFormExtended({
        disabled,
        mode: 'all',
        resolver: zodResolver(draftFormSchema),
        defaultValues: {
            billing_address_id: '',
            contact_person_id: '',
            customer_id: '',
            delivery_date: '',
            document_type: '',
            draft_order_id: '',
            due_date: '',
            notes: '',
            po_number: '',
            shipping_address_id: '',
            shipping_type_id: '',
            transaction_type: transactionDraftTypes[0],
        },
    });
}
